import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import { useAppSelector } from '../../../app/hooks'
import { AppDispatch } from '../../../app/store'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  GET_OPD_TREATMENTPLAN_ENDPOINT,
  POST_OPD_TREATMENTPLAN_ENDPOINT,
} from '../../../utils/Endpoints'
import {
  capitalize,
  REFERRAL_REQUIRED_CONDITION,
  toastMessages,
  TREATMENT_PLAN_NAMES,
  TREATMENT_PLAN_PHYSIO_TEXT_FIELDS,
  TREATMENT_PLAN_TYPES,
} from './constants'
import {
  isInitialLoadForOPDForms,
  isTreatmentPlanOpdFormSubmitted,
  selectTreatmentPlan,
  setIsTreatmentPlanOpdFormSubmit,
  setLoading,
  updateTreatmentPlan,
} from './opdFormsSlice'
import {
  opdFormDataFetchApi,
  opdFormDataSaveApi,
} from '../../../services/OpdFormsSTTService'
import {
  fetchSlotsDoctorsAsync,
  selectSlotsDoctors,
} from '../../slots/slotsSlice'

const styles = (theme: Theme) => ({
  textFieldStyle: {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      minWidth: '275px',
      height: '62px !important',
      padding: '4px !important',
    },
  },
  textInputFieldStyle: {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      minWidth: '275px',
      height: '20px !important',
      padding: '4px !important',
    },
  },
  selectTextFieldStyle: {
    '& .MuiInputBase-input': {
      fontSize: '14px',
      minWidth: '102px',
    },
  },
  fieldLabel: {
    fontSize: '12px',
    textAlign: 'right',
    paddingRight: '5px',
    [theme.breakpoints.between('sm', 'md')]: {
      minWidth: '120px',
    },
    [theme.breakpoints.up('md')]: {
      minWidth: '250px',
    },
  },
  textFieldWhiteBg: {
    backgroundColor: '#ffffff',
  },

  customFontBox: {
    '& .MuiTypography-root': {
      fontFamily: 'Noto Sans, sans-serif',
      color: '#363463',
    },
    '& .MuiButton-root': {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      textTransform: 'capitalize',
      width: '147px',
      margin: '2px',
      justifyContent: 'center',
      color: '#6c757d',
    },
    '& .MuiButton-contained': {
      backgroundColor: '#4682b4 !important',
      color: '#f1f3f5 !important',
      fontWeight: 600,
      border: '1px solid #4682b4 !important',
    },
  },
  submitButtonContainer: {
    [theme.breakpoints.up('md')]: {
      width: '83%',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '78%',
    },
  },
  doctorContent: {
    '& .MuiInputBase-input': {
      padding: '6px 4px !important',
    },
  },
  dateField: {
    [theme.breakpoints.up('md')]: {
      width: '562px',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '440px',
    },
  },
  dateInputFiled: {
    '& .MuiInputBase-input': {
      padding: '4px !important',
    },
  },
})

const TreatmentPlanPhysio = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const { id } = useParams()
  const visitType = 'physio'
  const treatmentPlan: any = useAppSelector(selectTreatmentPlan)
  const dispatch = useDispatch<AppDispatch>()
  const [fhirRecordId, setFhirRecordId] = useState<string | null>(null)
  const isInitialLoadForForm = useAppSelector(isInitialLoadForOPDForms)
  const doctors = useAppSelector(selectSlotsDoctors)
  const isFormSubmitted = useAppSelector(isTreatmentPlanOpdFormSubmitted)

  const initialValues = {
    treatmentPlan: treatmentPlan?.treatmentPlan || '',
    followUpAdvice: treatmentPlan?.followUpAdvice || '',
    followUpDate: treatmentPlan?.followUpDate || '',
    homeAdvice: treatmentPlan?.homeAdvice || '',
    referralRequired: treatmentPlan?.referralRequired || '',
    department: treatmentPlan?.department || '',
    doctorName: treatmentPlan?.nameOfDoctor || '',
    reasonReferral: treatmentPlan?.reasonReferral || '',
  }

  const validationSchema = Yup?.object({
    treatmentPlan: Yup?.string(),
    followUpAdvice: Yup?.string(),
    homeAdvice: Yup?.string(),
  })

  const formik: any = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleTreatmentPlanSubmitForm(values, setSubmitting)
    },
    enableReinitialize: true,
  })

  const handleTreatmentPlanSubmitForm = async (
    data: Record<string, any>,
    setSubmitting: any
  ) => {
    setSubmitting(true)

    const payload = {
      encounterId: id,
      visitType: `opd/${visitType}`,
      fhirRecordId: fhirRecordId,
      ...data,
    }

    const response = await opdFormDataSaveApi(
      POST_OPD_TREATMENTPLAN_ENDPOINT,
      payload
    )

    if (response?.success) {
      dispatch(setIsTreatmentPlanOpdFormSubmit(true))
      toast.success(response?.message, { autoClose: 800 })
    } else {
      toast.error(response?.message || toastMessages.Form_Submission_Error, {
        autoClose: 800,
      })
    }

    setSubmitting(false)
  }

  const getTreatmentPlan = useCallback(async () => {
    if (!isInitialLoadForForm && !isFormSubmitted) return
    const getTreatmentPlanUrl = `${GET_OPD_TREATMENTPLAN_ENDPOINT}${id}&visitType=opd/${visitType}`

    const response = await opdFormDataFetchApi(getTreatmentPlanUrl)

    if (response?.success) {
      const result = response?.data
      const fhirRecordId = response?.data?.fhirRecordId
      setFhirRecordId(fhirRecordId)

      dispatch(
        updateTreatmentPlan({
          treatmentPlan: result?.treatmentPlan,
          followUpAdvice: result?.followUpAdvice,
          homeAdvice: result?.homeAdvice,
          followUpDate: result?.followUpDate,
          referralRequired: result?.referralRequired,
          department: result?.department,
          nameOfDoctor: result?.nameOfDoctor,
          reasonReferral: capitalize(result?.reasonReferral),
        })
      )
    } else {
      toast.error(toastMessages.Fetch_Failed)
    }
    dispatch(setLoading(false))
  }, [dispatch, id, visitType, isInitialLoadForForm, isFormSubmitted])

  useEffect(() => {
    getTreatmentPlan()
    dispatch(fetchSlotsDoctorsAsync())
  }, [getTreatmentPlan, dispatch])

  return (
    <Box
      display='flex'
      flexDirection='column'
      gap={'10px'}
      className={classes?.customFontBox}
    >
      {TREATMENT_PLAN_PHYSIO_TEXT_FIELDS?.map((field) => (
        <Box
          key={field?.name}
          display='flex'
          flexDirection='row'
          alignItems='center'
          justifyContent='start'
          className={`${
            field?.type === TREATMENT_PLAN_TYPES?.DATE ? classes?.dateField : ''
          }`}
        >
          <Box
            className={classes?.fieldLabel}
            display='flex'
            alignItems='center'
            justifyContent='flex-end'
          >
            <Typography className={classes?.fieldLabel}>
              {field?.label}:
            </Typography>
          </Box>

          <Box>
            {field.type === TREATMENT_PLAN_TYPES?.TOGGLE ? (
              <>
                <Box display='flex' gap={'4px'} flexWrap='nowrap'>
                  {field?.options?.map((opt) => {
                    const isSelected =
                      formik?.values?.[field?.name] === opt?.value
                    return (
                      <Button
                        key={opt?.label}
                        variant={isSelected ? 'contained' : 'outlined'}
                        color={isSelected ? 'primary' : 'inherit'}
                        onClick={() =>
                          formik?.setFieldValue(field?.name, opt?.value)
                        }
                      >
                        {opt?.label}
                      </Button>
                    )
                  })}
                </Box>
                <Typography variant='caption' color='error' />
              </>
            ) : (
              <TextField
                fullWidth
                inputProps={{ 'data-testid': field?.name }}
                multiline={field?.type === TREATMENT_PLAN_TYPES?.TEXTAREA}
                rows={
                  field?.type === TREATMENT_PLAN_TYPES?.TEXTAREA ? 3 : undefined
                }
                type={
                  field?.type === TREATMENT_PLAN_TYPES?.DATE ? 'date' : 'text'
                }
                name={field?.name}
                value={
                  formik?.values[field?.name as keyof typeof formik.values] ||
                  ''
                }
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                InputLabelProps={{
                  shrink: field?.type === TREATMENT_PLAN_TYPES?.DATE,
                }}
                className={`${
                  field?.type !== TREATMENT_PLAN_TYPES?.DATE
                    ? classes?.textFieldStyle
                    : classes?.dateInputFiled
                } ${classes?.textFieldWhiteBg}`}
              />
            )}
          </Box>
        </Box>
      ))}

      {formik?.values?.referralRequired === REFERRAL_REQUIRED_CONDITION.Yes && (
        <>
          <Box
            display='flex'
            flexDirection='row'
            alignItems='center'
            justifyContent='start'
            width='100%'
          >
            <Box
              className={classes?.fieldLabel}
              display='flex'
              alignItems='center'
              justifyContent='flex-end'
            >
              <Typography className={classes?.fieldLabel}>
                Name of the Doctor:
              </Typography>
            </Box>

            <Box flex={1}>
              <TextField
                fullWidth
                select
                name='doctorName'
                value={formik?.values?.doctorName}
                onChange={(e) => {
                  const selectedDoctor = doctors?.find(
                    (doc) => doc?.name === e?.target?.value
                  )
                  formik?.setFieldValue('doctorName', e?.target?.value)
                  formik?.setFieldValue(
                    'department',
                    selectedDoctor?.specialty || ''
                  )
                }}
                onBlur={formik?.handleBlur}
                error={
                  formik?.touched?.doctorName &&
                  Boolean(formik?.errors?.doctorName)
                }
                helperText={
                  formik?.touched?.doctorName && formik?.errors?.doctorName
                }
                className={`${classes?.textInputFieldStyle} ${classes?.textFieldWhiteBg} ${classes?.doctorContent}`}
              >
                {doctors?.map((doc) => (
                  <MenuItem key={doc?.id} value={doc?.name}>
                    {doc?.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          <Box
            display='flex'
            flexDirection='row'
            alignItems='center'
            justifyContent='start'
            width='100%'
          >
            <Box
              className={classes?.fieldLabel}
              display='flex'
              alignItems='center'
              justifyContent='flex-end'
            >
              <Typography className={classes?.fieldLabel}>
                Department:
              </Typography>
            </Box>

            <Box flex={1}>
              <TextField
                fullWidth
                name='department'
                inputProps={{ 'data-testid': 'department' }}
                value={formik?.values?.department || ''}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                disabled
                className={`${classes?.textInputFieldStyle} ${classes?.textFieldWhiteBg}`}
              />
            </Box>
          </Box>

          <Box
            display='flex'
            flexDirection='row'
            alignItems='center'
            justifyContent='start'
            width='100%'
          >
            <Box
              className={classes?.fieldLabel}
              display='flex'
              alignItems='center'
              justifyContent='flex-end'
            >
              <Typography className={classes?.fieldLabel}>
                Reason Referral:
              </Typography>
            </Box>

            <Box flex={1}>
              <TextField
                fullWidth
                name={TREATMENT_PLAN_NAMES.ReasonReferral}
                value={formik?.values?.reasonReferral || ''}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                className={`${classes?.textInputFieldStyle} ${classes?.textFieldWhiteBg}`}
              />
            </Box>
          </Box>
        </>
      )}

      <Box
        display='flex'
        alignItems='center'
        justifyContent='flex-end'
        className={classes?.submitButtonContainer}
      >
        <Button
          variant='contained'
          color='primary'
          onClick={formik?.submitForm}
          className={classes?.submitBtn}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}

export default TreatmentPlanPhysio
